import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * FormSchemaService
 *
 * Centralized service for loading and caching form schemas.
 * Provides field labels and sheet names for auto-generating validation error messages.
 *
 * Purpose:
 * - Load form schemas from JSON files with HTTP caching
 * - Provide field label lookup by qname
 * - Provide sheet label lookup by sheet key
 * - Minimize HTTP requests through in-memory caching
 *
 * Usage:
 * 1. Inject service in components or other services
 * 2. Call loadSchema() to load a specific sheet schema
 * 3. Use getFieldLabel() and getSheetLabel() for error message generation
 */
@Injectable({
  providedIn: 'root'
})
export class FormSchemaService {
  private http = inject(HttpClient);

  // In-memory cache for loaded schemas
  private schemaCache: { [sheetKey: string]: any } = {};

  /**
   * Load schema from JSON file with caching
   *
   * @param sheetKey - Sheet identifier (e.g., "310000", "210000", "520000")
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns Promise resolving to schema object
   */
  async loadSchema(sheetKey: string, taxonomy: 'MFRS' | 'MPERS' = 'MFRS'): Promise<any> {
    const cacheKey = `${sheetKey}-${taxonomy}`;

    // Check cache first
    if (this.schemaCache[cacheKey]) {
      return this.schemaCache[cacheKey];
    }

    try {
      // Determine file path based on sheet key
      // Pattern: role-[sheetKey]-[taxonomy-suffix].form.json
      // Example: role-310000-mfrs.form.json, role-210000-mfrs.form.json
      const taxonomySuffix = taxonomy.toLowerCase();
      const filePath = `/assets/forms/role-${sheetKey}-${taxonomySuffix}.form.json`;

      // Load schema via HTTP
      const schema = await firstValueFrom(
        this.http.get<any>(filePath, { withCredentials: false })
      );

      // Cache the loaded schema
      this.schemaCache[cacheKey] = schema;

      return schema;
    } catch (error) {
      console.error(`Failed to load schema for sheet ${sheetKey} (${taxonomy}):`, error);
      return null;
    }
  }

  /**
   * Get field label from cached schema
   *
   * @param sheetKey - Sheet identifier
   * @param fieldQname - Field qname (e.g., "ifrs-full:ProfitLoss")
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns Field label or qname if not found
   */
  getFieldLabel(
    sheetKey: string,
    fieldQname: string,
    taxonomy: 'MFRS' | 'MPERS' = 'MFRS'
  ): string {
    const cacheKey = `${sheetKey}-${taxonomy}`;
    const schema = this.schemaCache[cacheKey];

    if (!schema || !schema.controls) {
      return fieldQname; // Fallback to qname if schema not loaded
    }

    // Find control by qname
    const control = schema.controls.find((control: any) => control.qname === fieldQname);

    return control?.label || fieldQname;
  }

  /**
   * Get sheet label from cached schema
   *
   * @param sheetKey - Sheet identifier
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns Sheet label or sheetKey if not found
   */
  getSheetLabel(sheetKey: string, taxonomy: 'MFRS' | 'MPERS' = 'MFRS'): string {
    const cacheKey = `${sheetKey}-${taxonomy}`;
    const schema = this.schemaCache[cacheKey];

    return schema?.label || sheetKey;
  }

  /**
   * Preload multiple schemas (useful for cross-sheet validation)
   *
   * @param sheetKeys - Array of sheet identifiers to preload
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns Promise resolving when all schemas are loaded
   */
  async preloadSchemas(sheetKeys: string[], taxonomy: 'MFRS' | 'MPERS' = 'MFRS'): Promise<void> {
    const loadPromises = sheetKeys.map(sheetKey => this.loadSchema(sheetKey, taxonomy));
    await Promise.all(loadPromises);
  }

  /**
   * Get cached schema object directly
   *
   * @param sheetKey - Sheet identifier (e.g., "410000")
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns Cached schema object or null if not cached
   */
  getCachedSchema(sheetKey: string, taxonomy: 'MFRS' | 'MPERS' = 'MFRS'): any | null {
    const cacheKey = `${sheetKey}-${taxonomy}`;
    return this.schemaCache[cacheKey] || null;
  }

  /**
   * Check if schema is already cached
   *
   * @param sheetKey - Sheet identifier
   * @param taxonomy - Taxonomy type ("MFRS" or "MPERS")
   * @returns true if schema is in cache
   */
  isCached(sheetKey: string, taxonomy: 'MFRS' | 'MPERS' = 'MFRS'): boolean {
    const cacheKey = `${sheetKey}-${taxonomy}`;
    return !!this.schemaCache[cacheKey];
  }

  /**
   * Clear cache (useful for testing or memory management)
   *
   * @param sheetKey - Optional: clear specific sheet only, or all if not provided
   * @param taxonomy - Optional: taxonomy type when clearing specific sheet
   */
  clearCache(sheetKey?: string, taxonomy?: 'MFRS' | 'MPERS'): void {
    if (sheetKey && taxonomy) {
      const cacheKey = `${sheetKey}-${taxonomy}`;
      delete this.schemaCache[cacheKey];
    } else {
      this.schemaCache = {};
    }
  }

  /**
   * Get all cached schema keys (for debugging)
   *
   * @returns Array of cached schema keys
   */
  getCachedKeys(): string[] {
    return Object.keys(this.schemaCache);
  }
}
